import { useMemo } from 'react';
import {
    OveragesWarningState,
    createOveragesWarningSlice,
} from 'contexts/account/createOveragesWarningSlice';
import {
    ConnectPayoutMethodBannerState,
    createConnectPayoutMethodBannerSlice,
} from 'contexts/onboarding/createConnectPayoutMethodSlice';
import {
    OnboardingRecommendedActionsState,
    createOnboardingRecommendedActionsSlice,
} from 'contexts/onboarding/createOnboardingRecommendedActionsSlice';
import {
    OnboardingRequiredChecklistState,
    createOnboardingRequiredChecklistSlice,
} from 'contexts/onboarding/createOnboardingRequiredChecklistSlice';
import { useCurrentAccountId } from 'customHooks/useCurrentAccountId';
import { StoreApi } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createWithEqualityFn as create, useStoreWithEqualityFn } from 'zustand/traditional';

type AccountStorageState = OnboardingRequiredChecklistState &
    OnboardingRecommendedActionsState &
    ConnectPayoutMethodBannerState &
    OveragesWarningState;

const createAccountLocalStorage = (accountId: number): StoreApi<AccountStorageState> => {
    return create<AccountStorageState>()(
        persist(
            (...params) => ({
                ...createOnboardingRequiredChecklistSlice(...params),
                ...createOnboardingRecommendedActionsSlice(...params),
                ...createOveragesWarningSlice(...params),
                ...createConnectPayoutMethodBannerSlice(...params),
                ...createOveragesWarningSlice(...params),
            }),
            {
                name: `givebutter-account-${accountId}-localstorage-state`,
                storage: createJSONStorage(() => localStorage),
            },
        ),
    );
};

const accountStoresCache = new Map<number, ReturnType<typeof createAccountLocalStorage>>();

const createAccountLocalStorageFactory = (storesCache: typeof accountStoresCache) => {
    return (accountId: number): StoreApi<AccountStorageState> => {
        if (!storesCache.has(accountId)) {
            storesCache.set(accountId, createAccountLocalStorage(accountId));
        }
        return storesCache.get(accountId)!;
    };
};

const getAccountStore = createAccountLocalStorageFactory(accountStoresCache);

const useAccountLocalStorage = <T,>(selector: (state: AccountStorageState) => T): T => {
    const accountId = useCurrentAccountId();

    const currentStore = useMemo(() => getAccountStore(accountId), [accountId]);

    return useStoreWithEqualityFn(currentStore, selector);
};
export { useAccountLocalStorage };
